import { useEffect, useRef, useState } from 'react'
import { ChevronDown, RefreshCcw, Search, X } from 'lucide-react'

/**
 * Canonical filter-row primitive — used by every list page so search,
 * dropdowns, date pickers and reset/clear actions look and behave the
 * same everywhere.
 *
 * Design rules (locked-in per the SaaS QA standard):
 *   • Row container has NO border / NO card background — just spacing.
 *     Pages render this directly under the page title.
 *   • Search input is a fully-rounded pill (h-9) with a sidebar-navy
 *     (#0a2349) hairline border + trailing × clear when non-empty, and a
 *     SEPARATE circular navy magnifier button sitting to its right (per
 *     the design mockup). Navy border + focus glow matches the app's navy
 *     sidebar chrome for a single visual family.
 *   • Dropdowns size to their LONGEST option label, not the currently
 *     selected one. So a dropdown with options "demo" / "marketing" /
 *     "All Workspaces" reads as wide as "All Workspaces" no matter
 *     which is picked — selecting a short option doesn't shrink the
 *     pill and selecting a long one doesn't blow it up. Pages with
 *     unusually long labels still get a consistent fit because the
 *     pill always reserves room for the widest entry.
 *     The button-shaped pill chrome stays — trailing chevron, native
 *     `<select>` overlaid invisibly for keyboard accessibility.
 *   • Date fields use the same pill shape with a leading calendar
 *     icon; the native picker drives the value.
 *   • Refresh / Clear buttons sit at the row's right edge.
 *
 * Usage:
 *   ```jsx
 *   <FilterRow>
 *     <FilterRow.Search value={q} onChange={setQ} placeholder="Search…" />
 *     <FilterRow.Select
 *       value={cat}
 *       onChange={setCat}
 *       placeholder="All categories"
 *       options={[{ value: '', label: 'All categories' }, …]}
 *     />
 *     <FilterRow.Spacer />
 *     <FilterRow.Refresh onClick={refetch} loading={isFetching} />
 *   </FilterRow>
 *
 *   <FilterRow secondary>
 *     <FilterRow.DateField label="From" value={from} onChange={setFrom} />
 *     <FilterRow.DateField label="To" value={to} onChange={setTo} />
 *     <FilterRow.Spacer />
 *     <FilterRow.Clear show={anyActive} onClick={clearAll} />
 *   </FilterRow>
 *   ```
 */

export function FilterRow({ secondary = false, className = '', children }) {
  // Spacing rationale:
  //   • Primary row: `mb-3` gap to the next sibling (a secondary row
  //     or the table). When a page renders ONLY a primary row, the
  //     mb-3 keeps a comfortable breathing room before the table.
  //   • Secondary row: keeps a tight `mt-3` from the primary row and
  //     adds `mb-5` so the table doesn't crowd the date pickers.
  // The previous spacing (`mb-4` / `mt-2`) bunched the second row
  // right under the first and pushed the table flush against the
  // date pickers — the user flagged that as too tight.
  //
  // Chrome rationale:
  //   The filter row used to float against the page canvas with no
  //   border. On platform pages (AI Usage, Storage, Companies) the
  //   row sits between KPI cards above and the data table below —
  //   both of which carry borders — so the unbordered filter felt
  //   like it leaked across the page width. Adding a card shell
  //   (border + rule + canvas tint + padding) groups the controls
  //   into one visual unit and matches the surrounding chrome.
  return (
    <div
      className={
        'flex flex-wrap items-center gap-2.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2.5 ' +
        (secondary ? 'mt-3 mb-5 ' : 'mb-3 ') +
        className
      }
    >
      {children}
    </div>
  )
}

/** Search input — fixed width, no border, leading icon, trailing × clear.
 *  Width default bumped from `w-72` (288 px) to `w-96` (384 px) so a
 *  full placeholder like "Search action / resource id…" or
 *  "Search by company name…" doesn't truncate the cursor area. Pages
 *  needing a different width can override via the `width` prop. */
function FilterSearch({
  value,
  onChange,
  placeholder = 'Search…',
  // The magnifier is `aria-hidden` and there is no visible <label>, so without
  // this the field's only accessible name is its placeholder — which vanishes
  // the moment the user types, leaving a screen reader announcing a bare
  // "edit text". Same `ariaLabel ?? placeholder` idiom as FilterSelect below.
  ariaLabel,
  // Full-width on mobile so the search pill never overflows the row on a
  // narrow screen; the fixed `w-96` (desktop) kicks in from `sm:` up, so
  // desktop is unchanged. `max-w-full` (below) caps any caller-passed width
  // too. Callers overriding `width` should follow the same responsive shape.
  width = 'w-full sm:w-96',
}) {
  // Layout: a single search pill with the magnifier as a LEADING icon INSIDE
  // the field (the icon sits within the search bar rather than as a separate
  // circle button beside it) and a trailing × to clear when there's text.
  return (
    <div className={'relative max-w-full ' + width}>
      <span
        aria-hidden="true"
        // z-10 keeps the icon ABOVE the input — the `fd-search-pill` has a
        // semi-opaque background + backdrop-filter that would otherwise paint
        // over (and blur away) a leading icon sitting behind it in the DOM.
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)]"
      >
        <Search size={15} />
      </span>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className="fd-search-pill w-full h-9 pl-9 pr-9 rounded-full text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-muted)] outline-none"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <X size={12} />
        </button>
      ) : null}
    </div>
  )
}

/** Dropdown — sizes to the LONGEST option label, not the currently
 *  selected one. Implementation: a CSS grid stack holds one cell per
 *  option label; the currently selected label is shown, the rest are
 *  there but invisible. The grid cell's intrinsic width is therefore
 *  the width of the widest option, so the pill never shrinks when a
 *  short option is picked. No JS measuring, no fonts-loaded race, no
 *  layout thrash on resize. */
function FilterSelect({
  value,
  onChange,
  options = [],
  placeholder,
  ariaLabel,
  // When true the control is greyed out and non-interactive. Used by
  // cascading filters (e.g. the audit Module → Action pair) where the
  // second dropdown stays inert until the first is chosen.
  disabled = false,
}) {
  // Visible label is the matching option, else the placeholder.
  const current = options.find((o) => o.value === value)
  const visibleLabel = current?.label ?? placeholder ?? '—'
  // Build the ghost-label list. We render EVERY option label invisibly
  // inside the pill (plus the placeholder if there is one) so the
  // pill's intrinsic width is forced to the widest of them. Anything
  // currently selected isn't rendered as a ghost — it's the visible
  // label.
  const ghosts = options.map((o) => o.label)
  if (placeholder && !options.some((o) => o.label === placeholder)) {
    ghosts.push(placeholder)
  }
  return (
    <span className="relative inline-flex items-center">
      {/* Native select first — invisibly overlaid for accessibility AND
       *  so the chevron below is a *following sibling* of the <select>.
       *  That sibling relationship is what the global rotation CSS
       *  (`select[data-open] ~ svg` in index.css, driven by
       *  dropdownArrowGlue.js) keys off to spin the chevron 180° while
       *  the native popup is open. Keep the chevron AFTER the select. */}
      <select
        aria-label={ariaLabel ?? placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={
          'absolute inset-0 w-full h-full opacity-0 ' +
          (disabled ? 'cursor-not-allowed' : 'cursor-pointer')
        }
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {/* Pill — what the user sees. The grid stack inside (`grid` +
       *  `[grid-template-areas:'stack']`) overlays every label in the
       *  same cell. The visible one is opaque; all the others are
       *  `invisible` (they still take up layout space so the cell
       *  sizes to the widest). Net effect: the pill is exactly as
       *  wide as the longest option, no JS required. */}
      <span
        className={
          'inline-flex items-center h-9 pl-3 pr-8 rounded-md bg-transparent border border-[color:var(--color-rule)] text-sm select-none transition-colors ' +
          (disabled
            ? 'opacity-50 cursor-not-allowed text-[color:var(--color-ink-muted)]'
            : 'cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
              (value
                ? 'text-[color:var(--color-ink-strong)]'
                : 'text-[color:var(--color-ink-muted)]'))
        }
      >
        <span className="grid">
          <span
            className="whitespace-nowrap"
            style={{ gridArea: '1 / 1' }}
          >
            {visibleLabel}
          </span>
          {ghosts
            .filter((g) => g !== visibleLabel)
            .map((g, i) => (
              <span
                key={i}
                aria-hidden="true"
                className="whitespace-nowrap invisible h-0 overflow-hidden"
                style={{ gridArea: '1 / 1' }}
              >
                {g}
              </span>
            ))}
        </span>
      </span>
      <ChevronDown
        size={14}
        aria-hidden="true"
        className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[color:var(--color-ink-muted)] shrink-0"
      />
    </span>
  )
}

/** Whether the browser exposes the date control's inner sub-fields, so we
 *  can hide its own format hint and paint ours in its place.
 *
 *  True on Chromium/WebKit. Firefox has no `::-webkit-datetime-edit`, so
 *  there we leave its native hint alone rather than stack a second one on
 *  top of it. */
const CAN_MASK_DATE_HINT =
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports('selector(::-webkit-datetime-edit)')

/** Date input — pill shaped. The browser's native calendar picker glyph
 *  (app-styled in index.css) is the single calendar icon + click target, so
 *  we DON'T add a separate leading icon here (that duplicated the glyph — two
 *  calendars per field). Label sits inline before the input.
 *
 *  The empty-state format hint is OURS, not the browser's. A native
 *  `<input type="date">` ignores `placeholder`, and the pattern it paints
 *  when empty follows the BROWSER's locale — an en-US desktop rendered
 *  "MM/DD/YYYY" (against the DD/MM/YYYY this app uses everywhere else)
 *  while Android browsers painted nothing at all, so the same field read
 *  differently on desktop and mobile (QA, 2026-09-01).
 *
 *  While the field is empty and unfocused we mask the native sub-fields
 *  (`.fd-date-empty` in index.css) and render "DD/MM/YYYY" ourselves.
 *  Focus restores the real sub-fields — and hides ours — so typing a date
 *  on desktop stays visible. */
function FilterDateField({ label, value, onChange, min, max }) {
  const showOwnHint = CAN_MASK_DATE_HINT && !value
  return (
    <label className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
      {label ? <span>{label}</span> : null}
      <span className="relative inline-flex items-center">
        <input
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          // Transparent + bordered to match the search input + select
          // pills above. The canvas-grey was a stand-alone treatment
          // that read as off-shell now that FilterRow carries its own
          // white card.
          // `min-w` so the native control is wide enough to show the full
          // DD-MM-YYYY value + the browser's own picker glyph without
          // truncating to "DD-MM-YYY".
          className={
            'peer h-9 min-w-[168px] pl-3 pr-3 rounded-md bg-transparent text-sm text-[color:var(--color-ink)] outline-none border border-[color:var(--color-rule)] focus:ring-1 focus:ring-[color:var(--color-accent)] focus:border-[color:var(--color-accent)] normal-case tracking-normal font-normal ' +
            (showOwnHint ? 'fd-date-empty' : '')
          }
        />
        {showOwnHint ? (
          // aria-hidden: the <label> already names the field, and the
          // control announces its own date semantics. This is a visual
          // format cue, not a second name.
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 text-[13px] leading-none font-normal normal-case tracking-normal text-[color:var(--color-ink-muted)] peer-focus:opacity-0"
          >
            DD/MM/YYYY
          </span>
        ) : null}
      </span>
    </label>
  )
}

/** Refresh icon button — sits at the row's right edge. */
/**
 * Manual refresh button.
 *
 * The spin is held for at least {@link MIN_SPIN_MS} after a click. Without
 * that floor the control read as broken (QA, 2026-09-01, raised on
 * Companies and asked to be checked across every module that has one): a
 * refetch against a local API resolves in well under 200ms, and because
 * every list passes `placeholderData: (prev) => prev` the old rows stay on
 * screen throughout — so when the data hasn't changed server-side there is
 * no row update AND no perceptible spinner. The request really did happen;
 * nothing on screen said so.
 *
 * `aria-busy` exposes the same state to assistive tech, and the button
 * stays disabled while spinning so a double-click can't queue two fetches.
 */
const MIN_SPIN_MS = 600

function FilterRefresh({ onClick, loading, title = 'Refresh' }) {
  const [minSpin, setMinSpin] = useState(false)
  const timerRef = useRef(null)

  // Clear a pending timer if the row unmounts mid-spin (e.g. the admin
  // navigates away) so React never sets state on an unmounted component.
  useEffect(() => () => clearTimeout(timerRef.current), [])

  function handleClick(event) {
    setMinSpin(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setMinSpin(false), MIN_SPIN_MS)
    onClick?.(event)
  }

  // Spin while EITHER the floor is running or the query is still in
  // flight, so a slow request keeps spinning past the floor.
  const spinning = Boolean(loading) || minSpin

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={spinning}
      title={title}
      aria-label={title}
      aria-busy={spinning}
      className="p-2 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50"
    >
      <RefreshCcw size={14} className={spinning ? 'animate-spin' : ''} />
    </button>
  )
}

/** Clear-all-filters chip. Renders only when `show` is true.
 *  `boxed` (opt-in) gives it the same hairline-bordered pill chrome as
 *  the Search / Select / Date controls, so it reads as a sibling filter
 *  box rather than a bare text link. Default stays borderless so the
 *  existing right-edge text-link treatment on other pages is unchanged. */
function FilterClear({ show = true, onClick, label = 'Clear all', boxed = false }) {
  if (!show) return null
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1 h-9 px-3 rounded-md text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer ' +
        (boxed ? 'border border-[color:var(--color-rule)]' : '')
      }
    >
      <X size={12} />
      {label}
    </button>
  )
}

/** Pushes subsequent children to the right edge of the row. */
function FilterSpacer() {
  return <span className="flex-1" />
}

FilterRow.Search = FilterSearch
FilterRow.Select = FilterSelect
FilterRow.DateField = FilterDateField
FilterRow.Refresh = FilterRefresh
FilterRow.Clear = FilterClear
FilterRow.Spacer = FilterSpacer
